/**
 * A candidate's CV documents — list, register-and-upload, and the access-reason-gated
 * download, WP-W2. `web/shapes/candidates.md` §7 is the agreed shape.
 *
 * Three things happen here, and only the third is sensitive:
 *
 *  - **List** the document *metadata* (`GET …/documents`) — never bytes, never a URL, a
 *    bare `{ data: [...] }` with no page, read whole.
 *  - **Register + upload** (`POST …/documents` → a presigned PUT, then the browser PUTs
 *    the bytes straight to object storage). The orchestration is the screen's; this
 *    panel only picks the file and the type.
 *  - **Download** (`GET …/documents/{docId}/download`) — the one sensitive, access-
 *    logged read. It needs an `X-Access-Reason`, so the reason field is the gate: each
 *    row's Download is **disabled until the reason is usable**, which is the one place a
 *    disabled control is honest ("you have not finished saying what you are doing"). The
 *    field says where the reason goes — the shared `access.*` sentence — because a box
 *    collecting one without saying that collects it under false pretences.
 *
 * The reason field validates the same way `reason.ts` does: at least ten characters
 * after trimming, and **Latin-1 only**, because a header value is a ByteString and a
 * Vietnamese reason would throw before the request left the tab. A diacritic reason is
 * refused here, in the field, with the reason said out loud.
 */

import { useState, type ReactNode } from 'react';

import { useI18n, type Translate } from '../../i18n';
import { formatInstant } from '../../i18n/datetime';
import type { CandidateDocumentType, RecruitingCandidateDocument } from '../../api/recruiting';
import { CANDIDATE_DOCUMENT_TYPES } from '../../api/recruiting';
import { DOC_TYPE_LABEL, type DownloadState, type UploadState } from '../../recruiting/candidate';
import {
  isHeaderSafe,
  isUsableReason,
  MAX_ACCESS_REASON,
  MIN_ACCESS_REASON,
} from '../../access/reason';
import type { QueryStatus } from '../../api/useApiQuery';
import type { WorkforceApiError } from '../../api/errors';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { EmptyState } from '../../states/EmptyState';
import { Skeleton } from '../../states/Skeleton';
import { StatusNote } from '../../states/StatusNote';

const BYTES_PER_KB = 1024;

export interface CandidateDocumentsPanelProps {
  readonly status: QueryStatus;
  readonly documents: readonly RecruitingCandidateDocument[] | null;
  readonly readError: WorkforceApiError | null;
  readonly onReload: () => void;
  readonly locale: string;
  readonly timezone: string;
  readonly registerState: UploadState;
  readonly onRegister: (file: File, docType: CandidateDocumentType) => void;
  readonly downloadState: DownloadState;
  readonly onDownload: (doc: RecruitingCandidateDocument, reason: string) => void;
}

export function CandidateDocumentsPanel({
  status,
  documents,
  readError,
  onReload,
  locale,
  timezone,
  registerState,
  onRegister,
  downloadState,
  onDownload,
}: CandidateDocumentsPanelProps): ReactNode {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState<CandidateDocumentType>('cv');
  const [reason, setReason] = useState('');

  const registering = registerState.status === 'working';
  const reasonUsable = isUsableReason(reason);
  const reasonUnencodable = reason.trim() !== '' && !isHeaderSafe(reason.trim());

  return (
    <section className="wp-section" aria-labelledby="w2-docs-heading" data-testid="w2-docs">
      <h3 className="wp-section__title" id="w2-docs-heading">
        {t('recruiting.candidates.documents.title')}
      </h3>
      <p className="wp-small wp-muted">{t('recruiting.candidates.documents.intro')}</p>

      {/* ── Register + upload ─────────────────────────────────────────────── */}
      <div className="wp-section__subsection" data-testid="w2-docs-register">
        <h4 className="wp-section__title">{t('recruiting.candidates.documents.register.title')}</h4>
        <p className="wp-small wp-muted">{t('recruiting.candidates.documents.register.intro')}</p>
        <form
          className="wp-filters"
          onSubmit={(event) => {
            event.preventDefault();
            if (file === null || registering) return;
            onRegister(file, docType);
          }}
        >
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w2-docs-file">
              {t('recruiting.candidates.documents.register.file')}
            </label>
            <input
              className="wp-field__input"
              id="w2-docs-file"
              data-testid="w2-docs-file"
              type="file"
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null);
              }}
            />
          </div>
          <div className="wp-filters__group">
            <label className="wp-field__label" htmlFor="w2-docs-type">
              {t('recruiting.candidates.documents.register.docType')}
            </label>
            <select
              className="wp-field__input"
              id="w2-docs-type"
              data-testid="w2-docs-type"
              value={docType}
              onChange={(event) => {
                setDocType(event.target.value as CandidateDocumentType);
              }}
            >
              {CANDIDATE_DOCUMENT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {t(DOC_TYPE_LABEL[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="wp-filters__actions">
            <button
              className="wp-button wp-button--primary"
              type="submit"
              data-testid="w2-docs-upload"
              disabled={file === null || registering}
            >
              {registering
                ? t('recruiting.candidates.documents.register.submitting')
                : t('recruiting.candidates.documents.register.submit')}
            </button>
            {file === null ? (
              <p className="wp-small wp-muted" data-testid="w2-docs-nofile">
                {t('recruiting.candidates.documents.register.noFile')}
              </p>
            ) : null}
          </div>
        </form>
        {registerState.status === 'failed' ? (
          <ApiErrorNote
            error={registerState.error}
            subject={t('recruiting.candidates.documents.subject')}
            testId="w2-docs-register-error"
          />
        ) : null}
        {registerState.status === 'done' ? (
          <StatusNote
            severity="low"
            title={t('recruiting.candidates.documents.register.ok', {
              filename: registerState.document.filename,
            })}
            testId="w2-docs-register-ok"
          />
        ) : null}
      </div>

      {/* ── The access reason, gating every download below ────────────────── */}
      {/* Downloading a document writes a row to the audit log, so the reason for it
          is the one thing on this screen drawn as an object rather than a rule: a
          medium wash with a 1px medium border on all four sides (DESIGN.md §4 rule
          5). Everything below it is behind it. */}
      <div className="wp-section__subsection wp-f-gate" data-testid="w2-docs-access">
        <h4 className="wp-section__title">{t('access.title')}</h4>
        <p>{t('access.why', { subject: t('recruiting.candidates.documents.subjectAccess') })}</p>
        <p className="wp-small wp-muted">{t('access.recorded')}</p>
        <div className="wp-field">
          <label className="wp-field__label" htmlFor="w2-docs-reason">
            {t('access.label')}
          </label>
          <input
            className="wp-field__input"
            id="w2-docs-reason"
            data-testid="w2-docs-reason"
            type="text"
            value={reason}
            maxLength={MAX_ACCESS_REASON}
            autoComplete="off"
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          <p className="wp-small wp-muted" data-testid="w2-docs-reason-rule">
            {t('access.rule', { min: MIN_ACCESS_REASON })}
          </p>
          {reasonUnencodable ? (
            <p className="wp-small" role="alert" data-testid="w2-docs-reason-latin1">
              {t('access.latin1')}
            </p>
          ) : null}
        </div>
      </div>

      {/* ── The list ──────────────────────────────────────────────────────── */}
      {status === 'loading' ? (
        <Skeleton
          rows={3}
          variant="block"
          label={t('recruiting.candidates.documents.subject')}
          testId="w2-docs-skeleton"
        />
      ) : null}
      {status === 'error' && readError !== null ? (
        <ApiErrorNote
          error={readError}
          onRetry={onReload}
          subject={t('recruiting.candidates.documents.subject')}
          testId="w2-docs-error"
        />
      ) : null}

      {documents === null ? null : documents.length === 0 ? (
        <EmptyState
          title={t('recruiting.candidates.documents.empty.title')}
          description={t('recruiting.candidates.documents.empty.body')}
          testId="w2-docs-empty"
        />
      ) : (
        <ul className="wp-rows" data-testid="w2-docs-list">
          {documents.map((doc) => (
            <li className="wp-row" key={doc.id} data-testid={`w2-doc-${doc.id}`}>
              <div className="wp-row__main">
                <p>{doc.filename}</p>
                <p className="wp-small wp-muted">
                  {t('recruiting.candidates.documents.meta', {
                    type:
                      doc.doc_type === null
                        ? t('recruiting.candidates.docType.none')
                        : t(DOC_TYPE_LABEL[doc.doc_type]),
                    size: formatSize(doc.file_size_bytes, t),
                  })}
                  {' · '}
                  {t('recruiting.candidates.documents.uploadedAt', {
                    when: formatInstant(doc.uploaded_at, locale, timezone),
                  })}
                </p>
              </div>
              <div className="wp-row__figure">
                <button
                  className="wp-button"
                  type="button"
                  data-testid={`w2-doc-download-${doc.id}`}
                  disabled={!reasonUsable || downloadState.status === 'requesting'}
                  onClick={() => {
                    onDownload(doc, reason);
                  }}
                >
                  {downloadState.status === 'requesting' && downloadState.docId === doc.id
                    ? t('recruiting.candidates.documents.downloading')
                    : t('recruiting.candidates.documents.download')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {reasonUsable ? null : (
        <p className="wp-small wp-muted" data-testid="w2-docs-reason-needed">
          {t('recruiting.candidates.documents.reasonNeeded', { min: MIN_ACCESS_REASON })}
        </p>
      )}

      {downloadState.status === 'refused' ? (
        <ApiErrorNote
          error={downloadState.error}
          subject={t('recruiting.candidates.documents.subjectAccess')}
          testId="w2-docs-download-error"
        />
      ) : null}
      {downloadState.status === 'delivered' ? (
        <StatusNote
          severity="low"
          title={t('recruiting.candidates.documents.delivered', {
            filename: downloadState.filename,
          })}
          testId="w2-docs-download-ok"
        />
      ) : null}
    </section>
  );
}

/** A file size in KB, or the "unknown" word when the server recorded none. */
function formatSize(bytes: number | null, t: Translate): string {
  if (bytes === null) return t('recruiting.candidates.documents.sizeUnknown');
  const kb = Math.max(1, Math.round(bytes / BYTES_PER_KB));
  return t('recruiting.candidates.documents.sizeKb', { kb });
}
